import { useState, useEffect, useRef } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector } from "react-redux";
import CustomModal from "../../components/CustomComponents/modal/customModal.jsx";
import {
  SelectionModalHeaderActions,
  SelectionModalEmptyCTA,
  SelectionModalRefreshButton,
} from "../../components/CustomComponents/modal/SelectionModalQuickLinks";

function ServiceTimeModal({
  isOpen,
  onClose,
  onSubmit,
  servicePeriods,
  selectedServiceTimes,
  fetchServicePeriods,
}) {
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const [tempSelectedTimes, setTempSelectedTimes] =
    useState(selectedServiceTimes);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const fetchRef = useRef(fetchServicePeriods);

  useEffect(() => {
    fetchRef.current = fetchServicePeriods;
  }, [fetchServicePeriods]);

  useEffect(() => {
    setTempSelectedTimes(selectedServiceTimes);
  }, [selectedServiceTimes, isOpen]);

  const handleRefresh = async () => {
    if (typeof fetchRef.current !== "function") return;
    try {
      setIsRefreshing(true);
      await fetchRef.current({ silent: true });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleToggle = (serviceTime) => {
    setTempSelectedTimes((prev) => {
      const exists = prev.some((item) => item.id === serviceTime.id);
      return exists
        ? prev.filter((item) => item.id !== serviceTime.id)
        : [...prev, serviceTime];
    });
  };

  const handleSubmit = () => {
    onSubmit(tempSelectedTimes);
    onClose();
  };

  const periods = Array.isArray(servicePeriods) ? servicePeriods : [];

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      title="Select Service Times"
      subtitle="Choose one or more service times for the dining area."
      showButton={true}
      isCentered={true}
      modalSize="lg"
      submitButtonText="Submit"
      headerActions={<SelectionModalHeaderActions variant="serviceTime" />}
    >
      <style>
        {`
          .service-time-option:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
            border-color: ${themeColor} !important;
          }
          .service-time-option.selected:hover {
            box-shadow: 0 4px 12px ${themeColor}40 !important;
          }
          .custom-checkbox:hover {
            border-color: ${themeColor} !important;
            transform: scale(1.05);
          }
        `}
      </style>

      <div className="d-flex align-items-stretch gap-2 mb-3">
        <div
          className="alert d-flex align-items-center flex-grow-1 mb-0"
          style={{
            backgroundColor: isDarkMode ? "#1a202c" : "#d1ecf1",
            borderColor: isDarkMode ? "#2d3748" : "#bee5eb",
            color: isDarkMode ? "#e2e8f0" : "#0c5460",
            border: `1px solid ${isDarkMode ? "#2d3748" : "#bee5eb"}`,
            borderRadius: "8px",
            padding: "12px 16px",
          }}
        >
          <i
            className="fa fa-info-circle me-2"
            style={{ color: isDarkMode ? "#63b3ed" : "#0c5460" }}
          />
          <span>
            Select the service times that will be available for this dining
            area.
          </span>
        </div>
        {typeof fetchServicePeriods === "function" && (
          <SelectionModalRefreshButton
            onClick={handleRefresh}
            loading={isRefreshing}
          />
        )}
      </div>

      <div className="row g-3 mb-4">
        {isRefreshing && periods.length === 0 ? (
          <div className="col-12 text-center py-4">
            <div
              className="spinner-border"
              role="status"
              style={{ color: themeColor }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <p
              className="mt-2 mb-0"
              style={{ color: isDarkMode ? "#a0aec0" : "#6c757d" }}
            >
              Loading service times...
            </p>
          </div>
        ) : periods.length === 0 ? (
          <div className="col-12">
            <SelectionModalEmptyCTA variant="serviceTime" />
          </div>
        ) : (
          periods.map((option) => (
            <div key={option.id} className="col-md-12 mb-3">
              <div
                className={`card border-0 shadow-sm service-time-option ${
                  tempSelectedTimes.some((item) => item.id === option.id)
                    ? "selected"
                    : ""
                }`}
                style={{
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  backgroundColor: tempSelectedTimes.some(
                    (item) => item.id === option.id
                  )
                    ? isDarkMode
                      ? `${themeColor}30`
                      : "#e8f5e8"
                    : isDarkMode
                      ? "#1a202c"
                      : "#fff",
                  border: tempSelectedTimes.some(
                    (item) => item.id === option.id
                  )
                    ? `2px solid ${themeColor}`
                    : `1px solid ${isDarkMode ? "#2d3748" : "#e9ecef"}`,
                  borderRadius: "12px",
                  padding: "20px",
                  position: "relative",
                  boxShadow: isDarkMode
                    ? tempSelectedTimes.some((item) => item.id === option.id)
                      ? `0 4px 12px ${themeColor}40`
                      : "0 2px 4px rgba(0,0,0,0.3)"
                    : tempSelectedTimes.some((item) => item.id === option.id)
                      ? `0 4px 12px ${themeColor}40`
                      : "0 2px 4px rgba(0,0,0,0.1)",
                }}
                onClick={() => handleToggle(option)}
              >
                <div className="d-flex align-items-center">
                  <div className="me-3">
                    <div
                      className="custom-checkbox"
                      style={{
                        width: "24px",
                        height: "24px",
                        border: `2px solid ${isDarkMode ? "#4a5568" : "#dee2e6"}`,
                        borderRadius: "6px",
                        backgroundColor: tempSelectedTimes.some(
                          (item) => item.id === option.id
                        )
                          ? themeColor
                          : isDarkMode
                            ? "#2d3748"
                            : "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.3s ease",
                        boxShadow: isDarkMode
                          ? "0 2px 4px rgba(0,0,0,0.3)"
                          : "0 1px 2px rgba(0,0,0,0.1)",
                      }}
                    >
                      {tempSelectedTimes.some(
                        (item) => item.id === option.id
                      ) && (
                        <i
                          className="fa fa-check text-white"
                          style={{ fontSize: "12px" }}
                        />
                      )}
                    </div>
                  </div>

                  <div className="flex-grow-1">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <h6
                          className="mb-1 fw-bold"
                          style={{
                            color: isDarkMode ? "#f7fafc" : "#1a202c",
                            fontSize: "16px",
                          }}
                        >
                          {option.sT_Name}
                        </h6>
                        {option.sT_Description && (
                          <p
                            className="small mb-0"
                            style={{
                              color: isDarkMode ? "#a0aec0" : "#4a5568",
                              fontSize: "14px",
                            }}
                          >
                            {option.sT_Description}
                          </p>
                        )}
                      </div>

                      {tempSelectedTimes.some(
                        (item) => item.id === option.id
                      ) && (
                        <div className="ms-3">
                          <div
                            className="badge d-flex align-items-center"
                            style={{
                              backgroundColor: themeColor,
                              color: "white",
                              fontSize: "0.75rem",
                              padding: "6px 12px",
                              borderRadius: "20px",
                              boxShadow: isDarkMode
                                ? `0 2px 4px ${themeColor}40`
                                : `0 1px 2px ${themeColor}40`,
                              fontWeight: "500",
                            }}
                          >
                            <i className="fa fa-check me-1" />
                            Selected
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <input
                  type="checkbox"
                  className="d-none"
                  id={`serviceTime-${option.id}`}
                  checked={tempSelectedTimes.some(
                    (item) => item.id === option.id
                  )}
                  onChange={() => handleToggle(option)}
                />
              </div>
            </div>
          ))
        )}
      </div>

      {tempSelectedTimes.length > 0 && (
        <div
          className="alert"
          style={{
            backgroundColor: isDarkMode ? "#1a202c" : "#d4edda",
            borderColor: isDarkMode ? "#2d3748" : "#c3e6cb",
            color: isDarkMode ? "#e2e8f0" : "#155724",
            border: `1px solid ${isDarkMode ? "#2d3748" : "#c3e6cb"}`,
            borderRadius: "8px",
            padding: "12px 16px",
            boxShadow: isDarkMode
              ? "0 2px 4px rgba(0,0,0,0.3)"
              : "0 1px 2px rgba(0,0,0,0.1)",
          }}
        >
          <i
            className="fa fa-check-circle me-2"
            style={{ color: isDarkMode ? "#68d391" : "#28a745" }}
          />
          <strong>{tempSelectedTimes.length}</strong> service time
          {tempSelectedTimes.length !== 1 ? "s" : ""} selected
        </div>
      )}
    </CustomModal>
  );
}

export default ServiceTimeModal;
